import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import ChatHistoryCollection, { ChatSession } from '@/collections/chatHistory';

export interface ChatMessage {
  id: string;
  content: string;
  timestamp: Date;
  isUser: boolean;
  agent?: {
    name: string;
    avatar: string;
  };
}

export interface Agent {
  id: string;
  name: string;
  description: string;
  avatar: string;
  category?: string;
  isRecommended?: boolean;
}

export interface ChatConversation {
  id: string;
  title: string;
  lastMessage: string;
  timestamp: Date;
  messages: ChatMessage[];
  agentId?: string;
  agentName?: string;
}

@Module({ namespaced: true })
export default class AiChatModule extends VuexModule {
  // Chat state
  public currentConversation: ChatMessage[] = [];
  public currentConversationId: string = '';
  public selectedAgent: Agent | null = null;
  public chatLoading: boolean = false;
  public isContinuedConversation: boolean = false;

  // History state
  public chatHistory: ChatConversation[] = [];
  public historyLoading: boolean = false;
  public historyError: string = '';
  public historySearchQuery: string = '';

  // UI state
  public activeTab: number = 0;
  public isFullscreen: boolean = false;
  public isVisible: boolean = false;

  // Private collection instance
  private chatHistoryCollection = new ChatHistoryCollection();

  @Mutation
  public SET_CURRENT_CONVERSATION(messages: ChatMessage[]) {
    this.currentConversation = messages;
  }

  @Mutation
  public ADD_MESSAGE(message: ChatMessage) {
    this.currentConversation.push(message);
  }

  @Mutation
  public SET_CONVERSATION_ID(id: string) {
    this.currentConversationId = id;
  }

  @Mutation
  public SET_SELECTED_AGENT(agent: Agent | null) {
    this.selectedAgent = agent;
  }

  @Mutation
  public SET_CHAT_LOADING(loading: boolean) {
    this.chatLoading = loading;
  }

  @Mutation
  public SET_CONTINUED_CONVERSATION(isContinued: boolean) {
    this.isContinuedConversation = isContinued;
  }

  @Mutation
  public SET_CHAT_HISTORY(history: ChatConversation[]) {
    this.chatHistory = history;
  }

  @Mutation
  public SET_HISTORY_LOADING(loading: boolean) {
    this.historyLoading = loading;
  }

  @Mutation
  public SET_HISTORY_ERROR(error: string) {
    this.historyError = error;
  }

  @Mutation
  public SET_HISTORY_SEARCH_QUERY(query: string) {
    this.historySearchQuery = query;
  }

  @Mutation
  public SET_ACTIVE_TAB(tab: number) {
    this.activeTab = tab;
  }

  @Mutation
  public SET_FULLSCREEN(fullscreen: boolean) {
    this.isFullscreen = fullscreen;
  }

  @Mutation
  public SET_VISIBLE(visible: boolean) {
    this.isVisible = visible;
  }

  @Mutation
  public REMOVE_CONVERSATION_FROM_HISTORY(conversationId: string) {
    this.chatHistory = this.chatHistory.filter((c) => c.id !== conversationId);
  }

  @Mutation
  public UPDATE_CONVERSATION_TITLE(payload: { id: string; title: string }) {
    const conversation = this.chatHistory.find((c) => c.id === payload.id);
    if (conversation) {
      conversation.title = payload.title;
    }
  }

  @Action
  public async loadChatHistory(payload?: { agentId?: string; searchQuery?: string }) {
    try {
      this.SET_HISTORY_LOADING(true);
      this.SET_HISTORY_ERROR('');

      const searchRequest = {
        q: payload?.searchQuery || '',
        limit: 50,
        agent_id: payload?.agentId || undefined,
      };

      const response = await this.chatHistoryCollection.searchHistory(searchRequest);

      // Convert ChatSession to ChatConversation format
      const conversations: ChatConversation[] = response.sessions.map((session) => ({
        id: session.session_id,
        title: session.title,
        lastMessage: session.last_message_preview || 'No messages',
        timestamp: new Date(session.updated_at),
        messages: [], // Will be loaded when conversation is selected
        agentId: session.agent_id,
        agentName: session.agent_name,
      }));

      this.SET_CHAT_HISTORY(conversations);

    } catch (error: any) {
      console.error('Failed to load chat history:', error);
      this.SET_HISTORY_ERROR('Failed to load chat history. Please try again.');
      this.SET_CHAT_HISTORY([]);
    } finally {
      this.SET_HISTORY_LOADING(false);
    }
  }

  @Action
  public async searchChatHistory(searchQuery: string) {
    this.SET_HISTORY_SEARCH_QUERY(searchQuery);

    // Use current selected agent if available
    const agentId = this.selectedAgent?.id;
    await this.loadChatHistory({ agentId, searchQuery });
  }

  @Action
  public async selectConversation(conversation: ChatConversation) {
    try {
      // Load full conversation history from backend
      const sessionDetail = await this.chatHistoryCollection.getSessionHistory(conversation.id);

      // Convert backend messages to frontend format
      const messages: ChatMessage[] = sessionDetail.messages.map((msg) => ({
        id: msg.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
        isUser: msg.role === 'user',
        agent: msg.role === 'assistant' ? {
          name: sessionDetail.agent_name,
          avatar: 'mdi-robot',
        } : undefined,
      }));

      // Update state
      this.SET_CURRENT_CONVERSATION(messages);
      this.SET_CONVERSATION_ID(conversation.id);
      this.SET_CONTINUED_CONVERSATION(true);

      // Restore agent context if available
      if (conversation.agentId && conversation.agentName) {
        const agent: Agent = {
          id: conversation.agentId,
          name: conversation.agentName,
          description: 'Previous conversation agent',
          avatar: 'mdi-robot',
        };
        this.SET_SELECTED_AGENT(agent);
      }

      this.SET_ACTIVE_TAB(0); // Switch to chat tab

    } catch (error: any) {
      console.error('Failed to load conversation details:', error);
      this.SET_HISTORY_ERROR('Failed to load conversation. Please try again.');
    }
  }

  @Action
  public async deleteConversation(conversationId: string) {
    try {
      await this.chatHistoryCollection.deleteSession(conversationId);
      this.REMOVE_CONVERSATION_FROM_HISTORY(conversationId);
    } catch (error: any) {
      console.error('Failed to delete conversation:', error);
      this.SET_HISTORY_ERROR('Failed to delete conversation. Please try again.');
    }
  }

  @Action
  public async renameConversation(payload: { id: string; title: string }) {
    try {
      const updatedSession = await this.chatHistoryCollection.updateSessionTitle(payload.id, payload.title);
      this.UPDATE_CONVERSATION_TITLE({ id: payload.id, title: updatedSession.title });
      return updatedSession;
    } catch (error: any) {
      console.error('Failed to rename conversation:', error);
      this.SET_HISTORY_ERROR('Failed to rename conversation. Please try again.');
      throw error;
    }
  }

  @Action
  public createNewChat() {
    this.SET_CURRENT_CONVERSATION([]);
    this.SET_CONVERSATION_ID(this.generateConversationId());
    this.SET_CONTINUED_CONVERSATION(false);
    this.SET_SELECTED_AGENT(null);
    this.SET_ACTIVE_TAB(0);
  }

  @Action
  public async refreshHistory() {
    // Clear cache and reload history
    this.chatHistoryCollection.clearCache();

    const agentId = this.selectedAgent?.id;
    const searchQuery = this.historySearchQuery;
    await this.loadChatHistory({ agentId, searchQuery });
  }

  @Action
  public addMessage(message: ChatMessage) {
    this.ADD_MESSAGE(message);

    // Refresh history after adding message to update cache
    setTimeout(() => {
      this.refreshHistory();
    }, 1000); // Small delay to allow backend to process
  }

  @Action
  public selectAgent(agent: Agent) {
    this.SET_SELECTED_AGENT(agent);
    this.createNewChat();
  }

  @Action
  public toggleChat() {
    this.SET_VISIBLE(!this.isVisible);
  }

  @Action
  public toggleFullscreen() {
    this.SET_FULLSCREEN(!this.isFullscreen);
  }

  @Action
  public setActiveTab(tab: number) {
    this.SET_ACTIVE_TAB(tab);
  }

  // Helper method - ideally this would be extracted to a utility
  private generateConversationId(): string {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Getters
  get hasMessages(): boolean {
    return this.currentConversation.length > 0;
  }

  get filteredHistory(): ChatConversation[] {
    if (!this.historySearchQuery) {
      return this.chatHistory;
    }

    const query = this.historySearchQuery.toLowerCase();
    return this.chatHistory.filter((conversation) =>
      conversation.title.toLowerCase().includes(query) ||
      conversation.lastMessage.toLowerCase().includes(query) ||
      (conversation.agentName && conversation.agentName.toLowerCase().includes(query)),
    );
  }

  get isHistoryEmpty(): boolean {
    return this.chatHistory.length === 0 && !this.historyLoading;
  }
}
